import {Injectable} from '@angular/core';
import {EnvService} from '../../models/env.service';
import {FilterObject} from '../../shared/models/FilterBarObject';
import {
  IsPinCodeAlreadyInUseResponse,
  UserConfigurationMetaData,
  UserConfigurationResult,
  UserCreateOrModifyResponse,
  UserDetailsResult, UserLastAccessEvent, UserLastAccessEventsResponse,
  UserQueryResult,
  UserResultData
} from '../models/user/users-query-result';
import {UserFilter} from '../store/users/users.model';
import {GraphQLClientService} from './graphql-client.service';
import {UserName} from '../models/user/user.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private graphQLClient: GraphQLClientService, private envServiceProvider: EnvService) {
  }

  public getUsers(page: number = 0, count: number = 0, filterObject: FilterObject<UserFilter> = null): Promise<UserResultData> {
    //todo: check how to send conditional query according to the number of the results we want to recieve
    const query = `
    query{
      users(
        page:${page}
        pageSize:${count}
        departmentId:${filterObject?.filter?.department}
        accessGroupId:${filterObject?.filter?.accessGroup}
        carParkingGroupId:${filterObject?.filter?.carParkingGroup}
        cardPlusCardGroupId:${filterObject?.filter?.cardAndCardGroups}
        accessLevelId:${filterObject?.filter?.accessLevels}
        isActive:${filterObject?.filter?.status}
        withPhoto:${this.envServiceProvider.environment.users.loadImages}
        nameContains:"${filterObject?.search ? filterObject?.search : ''}"
        typeId:${filterObject.filter?.type}
      ){
        results {
        identification outputGroupId lockdownImmunityGroups userCardAndCardGroupId parkingGroupId pinCode pinDuressCode id imageBase64 firstName lastName middleName departmentId departmentName accessGroupName
          accessGroupId accessLevelName userTypeId accessLevelId userDescription startDate validUntil
          phone email antipassbackImmunityTimezone isAccessGranted
          isAccessDenied isActive isHandicap antipassback accessLevelId isInterlockGroupImmunity isExtended
          operator{
            email site password autoLogoutTimeoutInMinutes
            operatorAuthorizes{
              events networks configuration operators usersAndCards visitors reports lockdown manualOutputGroups
            }
          }
          doors(page: 1, count: 20){
            results{
              id name type description accessDueTo
            }
            totalCount
          }
          credentials{
            id type typeId isActive description
          }
          employeeHost {
            comment hostEmployeeId hostDepartmentId
          }
          visitor {
            isAutoDisableOnExit enableAccessAreaId inactiveCardTypeId visitorUserId visitorDepartmentId
          }
          accessGroup{
            id tDesc
          }
          additionalAccessGroups{
              id tDesc
          }
          additionalReaders{
              tDesc iD reader {iD tDesc bReaderOut networkId networkDesc} timeZone {iD tDesc}
          }
        }
      totalCount
      }
    }`;
    return this.graphQLClient.requestAsync<UserQueryResult>(query).then((results) => {
      return results?.data?.users;
    }).catch(() => {
      return null;
    });
  }

  public getIdHolders(): Promise<UserName[]> {
    const query = `
    query{
      users(
        page:1
        pageSize:10000
        departmentId:null
        accessGroupId:null
        carParkingGroupId:null
        cardPlusCardGroupId:null
        accessLevelId:null
        isActive:true
        withPhoto:false
        nameContains:""
        typeId:1
      ){
        results {
         id firstName lastName departmentId
         }
      }
    }`;
    return this.graphQLClient.requestAsync<UserQueryResult>(query).then((results) => {
      return results?.data?.users.results;
    }).catch(() => {
      return null;
    });
  }

  public getUserDetailsById(userId: number): Promise<UserDetailsResult> {
    const query = `
      query{
        user(id:${userId})   {
          identification
          outputGroupId
          lockdownImmunityGroups
          userCardAndCardGroupId
          parkingGroupId
          pinCode
          pinDuressCode
          id
          imageBase64
          firstName
          lastName
          middleName
          departmentName
          departmentId
          accessGroupName
          accessGroupId
          accessLevelName
          userTypeId
          accessLevelId
          userDescription
          startDate
          validUntil
          phone
          email
          isExtended
          antipassbackImmunityTimezone
          dualAuthenticationForceTimezone
          dualAuthenticationImmunityTimezone
          dualAuthenticationTimezoneIsEnabled
          isAccessGranted
          isAccessDenied
          isActive
          isHandicap
          antipassback
          accessLevelId
          isInterlockGroupImmunity
          accessGroup{
              iD tDesc
          }
          additionalAccessGroups{
               iD tDesc
          }
          additionalReaders{
              tDesc iD reader {iD tDesc bReaderOut networkId networkDesc} timeZone {iD tDesc}
          }
          operator {
            autoLogoutTimeoutInMinutes
            email
            operatorAuthorizes {
              events
              networks
              configuration
              operators
              usersAndCards
              visitors
              reports
              lockdown
              manualOutputGroups
            }
          }
          doors(page: 1, count: 20) {
            results {
              id
              name
              type
              description
              outputStatusId
              accessDueTo
              outputOpenTimeSeconds
            }
            totalCount
          }
          credentials {
            id
            type
            typeId
            isActive
            wiegandTypeId
            cardCode
            facilityCode
            description
          }
          employeeHost {
            comment
            hostEmployeeId
            hostDepartmentId
          }
          visitor {
            isAutoDisableOnExit
            enableAccessAreaId
            inactiveCardTypeId
            visitorDepartmentId
            visitorUserId
          }
        }
      }`;
    return this.graphQLClient.requestAsync<UserDetailsResult>(query).then(({data}) => {
      return data;
    }).catch(() => {
      return null;
    });
  }
   public getUserEmailById(userId: number): Promise<UserDetailsResult> {
    const query = `
      query{
        user(id:${userId}){
          email
          operator{
            email
          }
        }
      }
    `;
    return this.graphQLClient.requestAsync<UserDetailsResult>(query).then(({data}) => {
      return data;
    }).catch(() => {
      return null;
    });
  }

  public getUserPhotoById(userId: number): Promise<UserDetailsResult> {
    const query = `
      query{
        user(id:${userId}){
        id imageBase64 departmentName
        }
      }
    `;
    return this.graphQLClient.requestAsync<UserDetailsResult>(query).then(({data}) => {
      return data;
    }).catch(() => {
      return null;
    });
  }

  public async getConfiguration(): Promise<UserConfigurationMetaData> {
    const query = `
      query{
        configurations{
          userConfigurations {
            eEventCategory {id description}
            outputGroups{id description}
            carParkingGroup{id description}
            cardAndCardGroups{id description}
            timezones {id description}
            accessGroups {
              id
              tDesc
              timezoneReaders {idReader tDescReader idTimeZone}
            }
            readers {iD tDesc bReaderOut networkId networkDesc}
            visitorDepartments{id description}
            departments{id description}
            accessLevels {id description}
            credentialTypes{id description}
            operatorAuthorizesStatus{id description}
            userTypes{id description}
            accessDueToTypes {id description}
            visitorDisableTypes{id description}
            accessAreas{id description}
            readersTypes {
              id
              description
              maxFacilityNumber
              maxCardNumber
              hasFacilityNumber
            }
          }
        }
      }
    `;
    return (await this.graphQLClient.requestAsync<UserConfigurationResult>(query))?.data.configurations.userConfigurations;
  }

  public async getPermissions(): Promise<any> {
    const query = `
      query{
        permissions{
            events lockdown manualOutputGroups networks operators reports usersAndCards visitors autoLogoutTimeoutInMinutes
        }
      }
    `;
    return (await this.graphQLClient.requestAsync<any>(query))?.data.permissions;
  }
  public async getUserLastAccessEvents(userId: number = 1): Promise<[UserLastAccessEvent]> {
    const query = `
      query{
        userLastAccessEvents(id:${userId}){
          results {
         doorDescription direction eventCategory accessDescription dateTime isAccessGranted
        }
        totalCount
        }
      }`;
    return this.graphQLClient.requestAsync<UserLastAccessEventsResponse>(query).then(({data}) => {
      return data.userLastAccessEvents.results;
    }).catch(() => {
      return null;
    });
  }

  async saveOrModifyUser(user: any): Promise<UserCreateOrModifyResponse> {
    const userData: any = {...user};
    if (userData?.operator) {
      delete userData.operator;
    }
    const query = `mutation{${user?.id ? 'update' : 'add'}(userData: ${JSON.stringify(userData).replace(/"(\w{2,})":/gm, '$1:')})}`;
    return (await this.graphQLClient.requestAsync<UserCreateOrModifyResponse>(query))?.data;
  }

  public async isPinCodeAlreadyInUse(user: any, pinCode: string): Promise<IsPinCodeAlreadyInUseResponse> {

    const userData = {employeeId: user.id, pinCode};

    const query = `mutation{isPinCodeAlreadyInUseAsync(userData: ${JSON.stringify(userData).replace(/"(\w{2,})":/gm, '$1:')})}`;
    return (await this.graphQLClient.requestAsync<IsPinCodeAlreadyInUseResponse>(query))?.data;
  }
}
